import React from "react";
import { Modal } from "antd";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Typography,
  Grid,
  Button,
} from "@mui/material";
import { useAuth } from "../../../../auth/auth";
import { format } from "date-fns";

interface ReferenceFormData {
  candidateName: string;
  tradeRole: string;
  companyWorkedFor: string;
  refereeName: string;
  refereePosition: string;
  contactNumber: string;
  emailAddress: string;
  dateFrom: string;
  dateTo: string;
  siteProject: string;
  reasonForLeaving: string;
  dutiesCarriedOut: string;
  timekeepingAndAttendance: string; // "0" | "1" | "2" | "3"
  timekeepingComments: string;
  attitudeAndWorkEthic: string;
  followedSiteRules: string; // "true" | "false"
  siteRulesComments: string;
  qualityOfWork: string; // "0" | "1" | "2" | "3"
  reliableForSiteWork: string; // "true" | "false"
  wouldReemploy: string; // "true" | "false"
  reemployComments: string;
  overallReferenceSummary: string;
  completionDate: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ReferenceFormData) => Promise<void>;
  initialData?: Partial<ReferenceFormData> & {
    expDate?: string;
    status?: number;
  };
  loading?: boolean;
  workerName: string;
}

const defaultValues: ReferenceFormData = {
  candidateName: "",
  tradeRole: "",
  companyWorkedFor: "",
  refereeName: "",
  refereePosition: "",
  contactNumber: "",
  emailAddress: "",
  dateFrom: "",
  dateTo: "",
  siteProject: "",
  reasonForLeaving: "",
  dutiesCarriedOut: "",
  timekeepingAndAttendance: "",
  timekeepingComments: "",
  attitudeAndWorkEthic: "",
  followedSiteRules: "",
  siteRulesComments: "",
  qualityOfWork: "",
  reliableForSiteWork: "",
  wouldReemploy: "",
  reemployComments: "",
  overallReferenceSummary: "",
  completionDate: "",
};

export const ReferenceFormModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  loading = false,
  workerName,
}) => {
  const { state } = useAuth();
  const { control, handleSubmit, reset } = useForm<ReferenceFormData>({
    defaultValues,
  });

  React.useEffect(() => {
    if (open) {
      const formatRating = (value?: number | string): string => {
        if (value === undefined || value === null) return "";
        return value.toString();
      };
      const formatBoolean = (value?: boolean | string): string => {
        if (value === undefined || value === null) return "";
        return value === true || value === "true" ? "true" : "false";
      };

      reset({
        ...defaultValues,
        ...initialData,
        candidateName: workerName,
        timekeepingAndAttendance: formatRating(initialData?.timekeepingAndAttendance),
        qualityOfWork: formatRating(initialData?.qualityOfWork),
        followedSiteRules: formatBoolean(initialData?.followedSiteRules),
        reliableForSiteWork: formatBoolean(initialData?.reliableForSiteWork),
        wouldReemploy: formatBoolean(initialData?.wouldReemploy),
        completionDate: initialData?.completionDate
          ? format(new Date(initialData.completionDate), "yyyy-MM-dd")
          : "",
        dateFrom: initialData?.dateFrom
          ? format(new Date(initialData.dateFrom), "yyyy-MM-dd")
          : "",
        dateTo: initialData?.dateTo
          ? format(new Date(initialData.dateTo), "yyyy-MM-dd")
          : "",
      });
    }
  }, [initialData, workerName, open, reset]);

  const onFormSubmit = (data: ReferenceFormData) => {
    onSubmit(data);
  };

  const resetModal = () => {
    reset(defaultValues);
  };

  const completedByName = state.user
    ? `${state.user.name} ${state.user.surname}`
    : "";

  return (
    <Modal
      title={`${initialData ? "Edit" : "Add"} Reference Form for ${workerName}`}
      open={open}
      onCancel={() => {
        resetModal();
        onClose();
      }}
      width={800}
      footer={null}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto", px: 1 }}>
          <Typography variant="h6" gutterBottom>
            Candidate & Referee Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="candidateName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Candidate Name"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="tradeRole"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Trade/Role"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="companyWorkedFor"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Company Worked For"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="refereeName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Referee Name"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="refereePosition"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Referee Position"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="contactNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Contact Number"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="emailAddress"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    type="email"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateFrom"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="From Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateTo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="To Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="siteProject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Site/Project"
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="reasonForLeaving"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Reason for Leaving"
                    multiline
                    rows={2}
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Reference Questions
          </Typography>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              1. What duties did the candidate carry out?
            </Typography>
            <Controller
              name="dutiesCarriedOut"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              2. How would you rate their timekeeping and attendance?
            </Typography>
            <FormControl component="fieldset">
              <Controller
                name="timekeepingAndAttendance"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    {[
                      { label: "Excellent", value: "0" },
                      { label: "Good", value: "1" },
                      { label: "Average", value: "2" },
                      { label: "Poor", value: "3" },
                    ].map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </FormControl>
            <Controller
              name="timekeepingComments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Comments"
                  multiline
                  rows={2}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              3. Attitude and work ethic
            </Typography>
            <Controller
              name="attitudeAndWorkEthic"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              4. Follow site rules, H&S, instructions?
            </Typography>
            <FormControl component="fieldset">
              <Controller
                name="followedSiteRules"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                )}
              />
            </FormControl>
            <Controller
              name="siteRulesComments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Comments"
                  multiline
                  rows={2}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              5. Rate quality of work
            </Typography>
            <Controller
              name="qualityOfWork"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  {[
                    { label: "Excellent", value: "0" },
                    { label: "Good", value: "1" },
                    { label: "Average", value: "2" },
                    { label: "Poor", value: "3" },
                  ].map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              6. Reliable and suitable for site work?
            </Typography>
            <Controller
              name="reliableForSiteWork"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              )}
            />
          </Box>

          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              7. Would you re-employ?
            </Typography>
            <Controller
              name="wouldReemploy"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              )}
            />
            <Controller
              name="reemployComments"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Comments"
                  multiline
                  rows={2}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Box>

          <Typography variant="h6" gutterBottom>
            Overall Reference Summary
          </Typography>
          <Controller
            name="overallReferenceSummary"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                size="small"
                sx={{ mb: 2 }}
              />
            )}
          />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                value={completedByName}
                fullWidth
                label="Completed By"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="completionDate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mt: 3,
            mb: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              resetModal();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            style={{ boxShadow: "none" }}
          >
            {loading
              ? "Saving..."
              : initialData
              ? "Update Reference"
              : "Add Reference"}
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
